import config from '../config/config';

const ATTRIBUTION_TEXT = config.common.attribution;

const ANCHOR_TAG = config.common.domTags.anchor;
const CANVAS_TAG = config.common.domTags.canvas;
const DIV_TAG = config.common.domTags.div;

const SNAPSHOT_ATTRIBUTION_CLASS = 'snapshot-attribution';
const IMAGE_MIME_PREFIX = 'image/';
const MAP_EVENT_IDLE = 'idle';
const CANVAS_CONTEXT_2D = '2d';
// 基準線を下端に設定（座標計算をわかりやすく）
const CANVAS_TEXT_BASELINE = 'bottom';

// キャンバス描画はCSSでは制御できないため
const DEFAULT_SNAPSHOT_FORMAT = 'png';
const DEFAULT_SNAPSHOT_QUALITY = 0.9;
const DEFAULT_FILENAME_PREFIX = 'mapion-snapshot-';

const DEFAULT_FONT_SIZE_PX = 20;
const DEFAULT_FONT_FAMILY = 'Arial, sans-serif';
const DEFAULT_BG_COLOR = 'rgba(255, 255, 255, 0.7)';
const DEFAULT_TEXT_COLOR = '#333';
const DEFAULT_PADDING_PX = 5;

// Node.nodeType の値例：1 = Element、3 = Text、8 = Comment、9 = Document
const NODE_TYPE_ELEMENT = Node.ELEMENT_NODE; // 1

// ファイル名用のISOタイムスタンプ生成
function isoTimestampForFilename(date = new Date()) {
  // 例: "2026-01-06T12:34:56.789Z" -> "2026-01-06T12-34-56"
  return date.toISOString().replace(/\..+$/, '').replace(/:/g, '-').replace(/Z$/, '');
}

/**
 * スナップショット関数
 */
export const takeMapSnapshot = (map, options = {}) => {
  return new Promise((resolve) => {
    if (!map) {
      resolve(null);
      return;
    }

    // デフォルトオプション
    const defaultOptions = {
      format: DEFAULT_SNAPSHOT_FORMAT,
      quality: DEFAULT_SNAPSHOT_QUALITY,
      filename: `${DEFAULT_FILENAME_PREFIX}${isoTimestampForFilename()}`
    };
    // optionsはスナップショット画像の「形式・品質・ファイル名」などを指定するためのもの
    const mergedOptions = { ...defaultOptions, ...options };

    // 地図の現在の状態を保存
    const currentState = {
      center: map.getCenter(),
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch()
    };

    let copyrightElement = null;
    let isProcessing = false;

    try {
      // 著作権表示を一時的に追加
      copyrightElement = addCopyrightLayer(map);

      // 地図の描画が完了するまで待機
      map.once(MAP_EVENT_IDLE, () => {
        if (isProcessing) return;
        isProcessing = true;

        try {
          // 元のキャンバスを取得
          const canvas = map.getCanvas();

          // 新しいキャンバスを作成
          const newCanvas = document.createElement(CANVAS_TAG);
          newCanvas.width = canvas.width;
          newCanvas.height = canvas.height;
          const ctx = newCanvas.getContext(CANVAS_CONTEXT_2D);

          // 地図を描画
          ctx.drawImage(canvas, 0, 0);

          // 著作権情報をキャンバスに追加
          if (copyrightElement) {
            addCopyrightToCanvas(ctx, newCanvas.width, newCanvas.height, copyrightElement);
          }

          // PNG形式で保存
          const dataUrl = newCanvas.toDataURL(`${IMAGE_MIME_PREFIX}${mergedOptions.format}`, mergedOptions.quality);
          // ファイル名
          const fileName = `${mergedOptions.filename}.${mergedOptions.format}`;

          // ダウンロードリンクを作成
          const link = document.createElement(ANCHOR_TAG);
          link.href = dataUrl;
          // 保存時のファイル名
          link.download = fileName;
          document.body.appendChild(link);
          // ダウンロード
          link.click();
          document.body.removeChild(link);
          resolve(dataUrl);
        } catch (error) {
          resolve(null);
        } finally {
          // 著作権表示を削除
          if (copyrightElement) {
            removeCopyrightLayer(copyrightElement);
          }
          // 地図を元の状態に戻す
          map.jumpTo(currentState);
          isProcessing = false;
        }
      });

      // 地図の再描画をトリガー
      map.triggerRepaint();

    } catch (error) {
      // エラー時のクリーンアップ
      if (copyrightElement) {
        removeCopyrightLayer(copyrightElement);
      }
      map.jumpTo(currentState);
      resolve(null);
    }
  });
};

// スナップショットに表示する著作権
export function addCopyrightLayer(map) {
  const el = document.createElement(DIV_TAG);
  el.className = SNAPSHOT_ATTRIBUTION_CLASS;
  el.textContent = ATTRIBUTION_TEXT;
  map.getContainer().appendChild(el);
  return el;
}

// 一時追加した著作権表示要素を取り除く
function removeCopyrightLayer(element) {
  if (!element) {
    return;
  }
  // nodeTypeでDOMノードかを判定
  const isNodeTypeElement = element.nodeType === NODE_TYPE_ELEMENT;
  // 同一ウィンドウ内で生成されたElementインスタンスかを判定（補助チェック）
  const isInstanceOfElement = element instanceof Element;
  // どちらの判定もfalseならDOM要素ではないので何もしない
  if (!isNodeTypeElement && !isInstanceOfElement) {
    return;
  }

  try {
    if (element.parentNode) {
      element.parentNode.removeChild(element);
    }
  } catch (e) {
  }
}

/**
 * addCopyrightToCanvas:
 * DOM 要素の computed style（CSSからの実際の適用値）を参照して、
 * キャンバスに同じ見た目の著作権テキストを描画する。
 *
 * 処理の流れ：
 * 1) element のフォント・背景色・文字色・padding を CSS から取得（フォールバックあり）
 * 2) canvas のフォント設定とテキストベースラインの設定
 * 3) 描画する背景矩形の幅・高さを計算（テキスト幅＋padding×2、行高≒font-size＋padding×2）
 * 4) キャンバス右下に背景矩形を描画
 * 5) 背景上にテキストを描画
 */
function addCopyrightToCanvas(ctx, width, height, element) {
  // element の CSS 適用値（computed style）を取得
  const styles = (element && window.getComputedStyle) ? window.getComputedStyle(element) : null;

  // フォントサイズ・ファミリ・色・パディングを取得（フォールバックあり）
  const fontSizeRaw = styles?.fontSize || `${DEFAULT_FONT_SIZE_PX}px`;
  const fontFamily = styles?.fontFamily || DEFAULT_FONT_FAMILY;
  const bgColor = styles?.backgroundColor || DEFAULT_BG_COLOR;
  const textColor = styles?.color || DEFAULT_TEXT_COLOR;
  const paddingRaw = styles?.padding || `${DEFAULT_PADDING_PX}px`;

  // フォントサイズの数値化
  const fontSizePx = parseFloat(fontSizeRaw) || DEFAULT_FONT_SIZE_PX;
  // padding の数値化（先頭値を縦横の両方に流用。必要なら上下・左右を個別に扱う）
  const paddingPx = parseFloat((paddingRaw.split(/\s+/)[0])) || DEFAULT_PADDING_PX;

  // キャンバスのフォント設定（"10px Arial" のように設定）
  ctx.font = `${fontSizePx}px ${fontFamily}`;
  // テキスト描画の基準線
  ctx.textBaseline = CANVAS_TEXT_BASELINE;

  // テキストの実測幅を取得
  const textMetrics = ctx.measureText(ATTRIBUTION_TEXT);

  // 背景矩形の幅・高さを計算
  // 幅: テキスト幅 + 左右の padding×2
  const rectWidth = Math.ceil(textMetrics.width + paddingPx * 2);
  // 高さ: 行高の目安として font-size + 上下の padding×2
  const rectHeight = Math.ceil(fontSizePx + paddingPx * 2);

  // 背景矩形の描画位置（右下に固定）
  const rectX = width - rectWidth - paddingPx;
  const rectY = height - rectHeight - paddingPx;

  // 背景の描画
  ctx.fillStyle = bgColor;
  ctx.fillRect(rectX, rectY, rectWidth, rectHeight);

  // テキストの描画位置（背景の左内側、下から padding 分だけ上）
  const textX = rectX + paddingPx;
  const textY = rectY + rectHeight - paddingPx;

  // テキストの描画
  ctx.fillStyle = textColor;
  ctx.fillText(ATTRIBUTION_TEXT, textX, textY);

}
